import React, { useState, useEffect } from 'react';
import { ExpensesYearProvider, useExpensesYear } from '../../context/ExpensesYearContext';
import { formatEuro } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea,
  Paper,
  Skeleton,
  Chip,
  Divider,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// Recharts imports restored for monthly trend & yearly analysis charts
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CategoryUsageChart from '../../components/CategoryUsageChart';
import { getProducts, getInvoices, InvoiceDto } from '../../services/expensesApi';
import { 
  getHomeExpenseGroups, 
  getAccounts, 
  getHomeExpenses,
  getCurrentMonthExpenses,
  getCurrentYearExpenses
} from '../../services/homeExpensesApi';
import { AccountDto, HomeExpenseDto, HomeExpenseGroupDto, ExpenseType } from '../../types/homeExpenses';
import EnhancedMonthlyPivot from './components/EnhancedMonthlyPivot';
import CategoryMonthComparisonChart from './components/CategoryMonthComparisonChart';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';
import { useAuth } from '../auth/AuthContext';

interface DashboardStats {
  totalProducts: number;
  totalInvoices: number;
  totalValue: number;
  lowStockProducts: number;
  totalExpenseGroups: number;
  // Enhanced Account statistics with balance information
  totalAccounts: number;
  primaryAccount: AccountDto | null;
  accountBalances: Array<{ account: AccountDto; balance: number; transactionCount: number; lastTransactionDate?: string }>;
  totalNetWorth: number;
  positiveBalanceAccounts: number;
  // Enhanced Home Expenses statistics
  totalHomeExpenses: number;
  currentMonthIncome: number;
  currentMonthExpenses: number;
  currentMonthNet: number;
  currentYearIncome: number;
  currentYearExpenses: number;
  currentYearNet: number;
  // Income vs Expense breakdown
  monthlyTrend: Array<{ month: string; income: number; expenses: number; net: number; monthName: string }>;
  topExpenseCategories: Array<{ groupName: string; amount: number; percentage: number; transactionCount: number }>;
  topIncomeCategories: Array<{ groupName: string; amount: number; percentage: number; transactionCount: number }>;
  recentHomeExpenses: HomeExpenseDto[];
  // Business data (kept for completeness)
  monthlyExpenses: Array<{ month: string; amount: number }>;
  topProducts: Array<{ name: string; quantity: number }>;
  recentInvoices: InvoiceDto[];
  // Chart data
  expenseGroupPercentages: Array<{ groupName: string; percentage: number; amount: number; color: string }>;
  // Yearly analysis (monthly breakdown only retained; quarterly breakdown intentionally still removed per request scope)
  monthlyYearBreakdown: Array<{ month: number; monthName: string; income: number; expenses: number; net: number; transactionCount: number }>;
  availableYears: number[];
}

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', 
  '#ff8042', '#0088fe', '#00c49f', '#ffbb28', '#ff8888',
  '#8dd1e1', '#d084d0', '#87d068', '#ffc658', '#ffb347'
];

const ExpensesPageInner: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { year: selectedYear, setYear: setSelectedYear, availableYears } = useExpensesYear();
  const [allHomeExpenses, setAllHomeExpenses] = useState<HomeExpenseDto[]>([]);
  const [allExpenseGroups, setAllExpenseGroups] = useState<HomeExpenseGroupDto[]>([]);
  const { settings } = useAuth();
  // Opens on the type chosen in "A minha conta"
  const [expenseType, setExpenseType] = useState<ExpenseType>(
    settings?.defaultExpenseType === 'Personal' ? ExpenseType.Personal : ExpenseType.Home);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [products, invoices, expenseGroups, accounts, homeExpenses, currentMonthHomeExpenses, currentYearHomeExpenses] = await Promise.all([
          getProducts(),
          getInvoices(),
          getHomeExpenseGroups(expenseType),
          getAccounts(expenseType),
          getHomeExpenses(expenseType),
          getCurrentMonthExpenses(expenseType),
          getCurrentYearExpenses(expenseType)
        ]);

  // Persist raw data for Monthly Pivot component
  setAllHomeExpenses(homeExpenses);
  setAllExpenseGroups(expenseGroups);

  // Calculate existing stats
        const totalProducts = products.length;
        const totalInvoices = invoices.length;
        const totalValue = invoices.reduce((sum, invoice) => sum + invoice.total, 0);
        const lowStockProducts = products.filter(p => p.quantity < 10).length;
        const totalExpenseGroups = expenseGroups.length;

        const computedYears = Array.from(new Set(homeExpenses.map(expense => new Date(expense.date).getFullYear()))).sort((a, b) => b - a);
        if (!availableYears.length && computedYears.length) {
          setSelectedYear(computedYears[0]);
        }

        // Yearly analysis data for selected year
        const selectedYearExpenses = homeExpenses.filter(expense => new Date(expense.date).getFullYear() === selectedYear);

        // Monthly breakdown for selected year (restored)
        const monthlyYearBreakdown: Array<{ month: number; monthName: string; income: number; expenses: number; net: number; transactionCount: number }> = [];
        for (let month = 0; month < 12; month++) {
          const monthName = new Date(selectedYear, month, 1).toLocaleDateString('pt-PT', { month: 'long' });
          const monthExpenses = selectedYearExpenses.filter(expense => {
            const expenseDate = new Date(expense.date);
            return expenseDate.getMonth() === month;
          });
          const income = monthExpenses.filter(e => e.isIncome).reduce((sum, e) => sum + e.amount, 0);
          const expenses = Math.abs(monthExpenses.filter(e => !e.isIncome).reduce((sum, e) => sum + e.amount, 0));
            const net = income - expenses;
          monthlyYearBreakdown.push({
            month: month + 1,
            monthName,
            income,
            expenses,
            net,
            transactionCount: monthExpenses.length
          });
        }

        // Keep quarters array only for potential future use (quarterly chart still intentionally removed)
        const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

        // Account statistics with balance calculation
        const accountBalances = accounts.map(account => {
          const accountExpenses = homeExpenses.filter(expense => expense.account === account.name);
          // Calculate balance properly using isIncome boolean
          const balance = accountExpenses.reduce((sum, expense) => {
            if (expense.isIncome) {
              return sum + expense.amount; // Add income
            } else {
              return sum - expense.amount; // Subtract expenses
            }
          }, 0);
          const transactionCount = accountExpenses.length;
          const lastTransaction = accountExpenses.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
          
          return {
            account,
            balance,
            transactionCount,
            lastTransactionDate: lastTransaction?.date
          };
        });

        const totalNetWorth = accountBalances.reduce((sum, ab) => sum + ab.balance, 0);
        const positiveBalanceAccounts = accountBalances.filter(ab => ab.balance > 0).length;
        const primaryAccount = accounts.find(acc => acc.isPrimary) || null;

        // Enhanced Home Expenses statistics
        const totalHomeExpenses = homeExpenses.length;
        
        // Current month calculations (income/expense split)
        const currentMonthIncome = currentMonthHomeExpenses
          .filter(expense => expense.isIncome)
          .reduce((sum, expense) => sum + expense.amount, 0);
        const currentMonthExpenses = Math.abs(currentMonthHomeExpenses
          .filter(expense => !expense.isIncome)
          .reduce((sum, expense) => sum + expense.amount, 0));
        const currentMonthNet = currentMonthIncome - currentMonthExpenses;

        // Current year calculations (income/expense split)
        const currentYearIncome = currentYearHomeExpenses
          .filter(expense => expense.isIncome)
          .reduce((sum, expense) => sum + expense.amount, 0);
        const currentYearExpenses = Math.abs(currentYearHomeExpenses
          .filter(expense => !expense.isIncome)
          .reduce((sum, expense) => sum + expense.amount, 0));
        const currentYearNet = currentYearIncome - currentYearExpenses;

        // Monthly trend (last 6 months) restored
        const monthlyTrend: Array<{ month: string; income: number; expenses: number; net: number; monthName: string }> = [];
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          const monthName = date.toLocaleDateString('pt-PT', { month: 'short', year: 'numeric' });
          const monthExpenses = homeExpenses.filter(expense => {
            const expenseDate = new Date(expense.date);
            return expenseDate.getFullYear() === date.getFullYear() && expenseDate.getMonth() === date.getMonth();
          });
          const income = monthExpenses.filter(e => e.isIncome).reduce((sum, e) => sum + e.amount, 0);
          const expenses = Math.abs(monthExpenses.filter(e => !e.isIncome).reduce((sum, e) => sum + e.amount, 0));
          const net = income - expenses;
          monthlyTrend.push({ month: monthKey, income, expenses, net, monthName });
        }

        // Top expense and income categories (current year)
        const expenseCategories = expenseGroups.filter(g => !g.isIncomeGroup);
        const incomeCategories = expenseGroups.filter(g => g.isIncomeGroup);

        const topExpenseCategories = expenseCategories.map(group => {
          const groupExpenses = currentYearHomeExpenses.filter(expense => 
            expense.group === group.name && !expense.isIncome
          );
          const amount = Math.abs(groupExpenses.reduce((sum, expense) => sum + expense.amount, 0));
          const transactionCount = groupExpenses.length;
          const percentage = currentYearExpenses > 0 ? (amount / currentYearExpenses) * 100 : 0;
          
          return { groupName: group.name, amount, percentage, transactionCount };
        }).filter(g => g.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 5);

        const topIncomeCategories = incomeCategories.map(group => {
          const groupIncome = currentYearHomeExpenses.filter(expense => 
            expense.group === group.name && expense.isIncome
          );
          const amount = groupIncome.reduce((sum, expense) => sum + expense.amount, 0);
          const transactionCount = groupIncome.length;
          const percentage = currentYearIncome > 0 ? (amount / currentYearIncome) * 100 : 0;
          
          return { groupName: group.name, amount, percentage, transactionCount };
        }).filter(g => g.amount > 0).sort((a, b) => b.amount - a.amount).slice(0, 5);

        // Group expenses by group for pie chart (current year)
        const expenseGroupPercentages = topExpenseCategories.map((item, index) => ({
          groupName: item.groupName,
          percentage: item.percentage,
          amount: item.amount,
          color: COLORS[index % COLORS.length]
        }));

        // Quarterly breakdown for current year
        const quarterlyBreakdown = quarters.map((quarter, index) => {
          const startMonth = index * 3;
          const endMonth = startMonth + 2;
          
          const quarterExpenses = currentYearHomeExpenses.filter(expense => {
            const month = new Date(expense.date).getMonth();
            return month >= startMonth && month <= endMonth;
          });
          
          const income = quarterExpenses.filter(e => e.isIncome).reduce((sum, e) => sum + e.amount, 0);
          const expenses = Math.abs(quarterExpenses.filter(e => !e.isIncome).reduce((sum, e) => sum + e.amount, 0));
          const net = income - expenses;
          
          return { quarter, income, expenses, net };
        });

        // Recent home expenses (last 6)
        const recentHomeExpenses = homeExpenses
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 6);

        // Business data
        const topProducts = products
          .sort((a, b) => b.quantity - a.quantity)
          .slice(0, 5)
          .map(p => ({ name: p.name, quantity: p.quantity }));

        const recentInvoices = invoices
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 3);

        setStats({
          totalProducts,
          totalInvoices,
          totalValue,
          lowStockProducts,
          totalExpenseGroups,
          // Enhanced Account statistics
          totalAccounts: accounts.length,
          primaryAccount,
          accountBalances,
          totalNetWorth,
          positiveBalanceAccounts,
          // Enhanced Home Expenses statistics
          totalHomeExpenses,
          currentMonthIncome,
          currentMonthExpenses,
          currentMonthNet,
          currentYearIncome,
          currentYearExpenses,
          currentYearNet,
          // Trend and category data
          monthlyTrend,
          topExpenseCategories,
          topIncomeCategories,
          recentHomeExpenses,
          // Business data
          monthlyExpenses: [],
          topProducts,
          recentInvoices,
          // Chart data
          expenseGroupPercentages,
          // Yearly monthly breakdown (quarterly omitted intentionally)
          monthlyYearBreakdown,
          availableYears
        });
      } catch (err) {
        setError('Falha ao carregar os dados do painel');
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedYear, expenseType]);

  const sections = [
    {
      title: 'Despesas da casa',
      description: 'Acompanhe as despesas e receitas diárias com filtros avançados',
      icon: <Box component="span" sx={{ fontSize: 48, color: 'primary.main' }} role="img" aria-label="Despesas da casa">🏠</Box>,
      path: '/expenses/home-expenses',
      count: stats?.totalHomeExpenses,
  additionalInfo: `${formatEuro(stats?.currentMonthExpenses || 0)} este mês`
    },
    {
      title: 'Contas',
      description: 'Gerir contas financeiras com cores e prioridades',
      icon: <AccountBalanceIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/expenses/accounts',
      count: stats?.totalAccounts,
      additionalInfo: stats?.primaryAccount ? `Principal: ${stats.primaryAccount.name}` : 'Sem conta principal'
    },
    {
      title: 'Produtos',
      description: 'Gerir o catálogo de produtos e o inventário',
      icon: <InventoryIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/expenses/products',
      count: stats?.totalProducts,
      additionalInfo: stats?.lowStockProducts ? `${stats.lowStockProducts} com pouco stock` : undefined
    },
    {
      title: 'Faturas',
      description: 'Criar, editar e gerir faturas com anexo de documento',
      icon: <ReceiptIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/expenses/invoices',
      count: stats?.totalInvoices,
  additionalInfo: `${formatEuro(stats?.totalValue || 0)} de valor total`
    },
    {
      title: 'Grupos de despesas',
      description: 'Organizar despesas e receitas em categorias',
      icon: <CategoryIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/expenses/groups',
      count: stats?.totalExpenseGroups
    },
    {
      title: 'Locais',
      description: 'Organizar locais de armazenamento, áreas e prateleiras',
      icon: <LocationOnIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/expenses/locations'
    }
  ];

  if (error) {
    return (
      <Box>
        <PageHeader
          title="Painel de gestão de despesas"
          actions={
            <FormControlLabel
              control={
                <Switch
                  checked={expenseType === ExpenseType.Personal}
                  onChange={(e) => setExpenseType(e.target.checked ? ExpenseType.Personal : ExpenseType.Home)}
                  color="primary"
                />
              }
              label={expenseType === ExpenseType.Personal ? 'Despesas pessoais' : 'Despesas da casa'}
            />
          }
        />
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <AutoGrid min={280} gap={3}>
          {sections.map((section) => (
            <Card
              key={section.title}
              elevation={2}
            >
              <CardActionArea onClick={() => navigate(section.path)}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ mb: 2 }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {section.title}
                    {section.count !== undefined && (
                      <Chip 
                        label={section.count} 
                        size="small" 
                        sx={{ ml: 1 }} 
                        color="primary"
                      />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {section.description}
                  </Typography>
                  {section.additionalInfo && (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
                      {section.additionalInfo}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </AutoGrid>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Painel de gestão de despesas"
        subtitle="Resumo da gestão de despesas com indicadores principais e ações rápidas."
        actions={
          <FormControlLabel
            control={
              <Switch
                checked={expenseType === ExpenseType.Personal}
                onChange={(e) => setExpenseType(e.target.checked ? ExpenseType.Personal : ExpenseType.Home)}
                color="primary"
              />
            }
            label={expenseType === ExpenseType.Personal ? 'Despesas pessoais' : 'Despesas da casa'}
          />
        }
      />

      {loading ? (
        <AutoGrid min={150} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" height={32} />
              </CardContent>
            </Card>
          ))}
        </AutoGrid>
      ) : (
        <>
          {/* Enhanced Summary Cards - Financial Overview */}
          <AutoGrid min={150} sx={{ mb: 4 }}>
            {/* Current Month Net */}
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Saldo do mês
                    </Typography>
                    <Typography variant="h4" color={stats?.currentMonthNet && stats.currentMonthNet >= 0 ? 'success.main' : 'error.main'}>
                      {formatEuro(stats?.currentMonthNet || 0)}
                    </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${formatEuro(stats?.currentMonthIncome ?? 0)} de receitas / ${formatEuro(stats?.currentMonthExpenses ?? 0)} de despesas`}
          </Typography>
                  </Box>
                  <TrendingUpIcon color={stats?.currentMonthNet && stats.currentMonthNet >= 0 ? 'success' : 'error'} sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>
            {/* Recent Transactions & Recent Invoices sections removed */}
            {/* Recent Invoices removed */}

            {/* Current Year Net */}
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Saldo do ano
                    </Typography>
                    <Typography variant="h4" color={stats?.currentYearNet && stats.currentYearNet >= 0 ? 'success.main' : 'error.main'}>
                      {formatEuro(stats?.currentYearNet || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`${formatEuro(stats?.currentYearIncome || 0)} de entradas - ${formatEuro(stats?.currentYearExpenses || 0)} de saídas`}
                    </Typography>
                  </Box>
                  <TrendingDownIcon color={stats?.currentYearNet && stats.currentYearNet >= 0 ? 'success' : 'error'} sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>

            {/* Total Net Worth */}
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Património líquido total
                    </Typography>
                    <Typography variant="h4" color={stats?.totalNetWorth && stats.totalNetWorth >= 0 ? 'success.main' : 'error.main'}>
                      {formatEuro(stats?.totalNetWorth || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Em {stats?.totalAccounts || 0} contas
                    </Typography>
                  </Box>
                  <AccountBalanceIcon color="primary" sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>

            {/* Total Transactions */}
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total de transações
                    </Typography>
                    <Typography variant="h4">
                      {stats?.totalHomeExpenses || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats?.positiveBalanceAccounts || 0} contas com saldo positivo
                    </Typography>
                  </Box>
                  <Box component="span" sx={{ fontSize: 40, color: 'primary.main' }} role="img" aria-label="Transações">📊</Box>
                </Stack>
              </CardContent>
            </Card>
          </AutoGrid>

          {/* Account Balance Summary */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
            💰 Saldos das contas
          </Typography>
          <AutoGrid min={260} gap={3} sx={{ mb: 4 }}>
            {stats?.accountBalances?.map((accountBalance, index) => (
              <Card 
                key={accountBalance.account.publicId} 
                sx={{
                  border: accountBalance.account.isPrimary ? 2 : 1,
                  borderColor: accountBalance.account.isPrimary ? 'primary.main' : 'divider'
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" noWrap>
                      {accountBalance.account.name}
                    </Typography>
                    {accountBalance.account.isPrimary && (
                      <Chip label="Principal" color="primary" size="small" />
                    )}
                  </Stack>
                  
                  <Typography 
                    variant="h4" 
                    color={accountBalance.balance >= 0 ? 'success.main' : 'error.main'}
                    sx={{ mb: 1 }}
                  >
                    {formatEuro(accountBalance.balance)}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {accountBalance.transactionCount} transações
                  </Typography>
                  
                  {accountBalance.lastTransactionDate && (
                    <Typography variant="caption" color="text.secondary">
                      Última: {formatDate(accountBalance.lastTransactionDate)}
                    </Typography>
                  )}
                  
                  {accountBalance.account.color && (
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 4, 
                        backgroundColor: accountBalance.account.color, 
                        borderRadius: 1, 
                        mt: 1 
                      }} 
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </AutoGrid>

          {/* Unified Year Selector & Analysis Blocks Reordered */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Ano:</Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={selectedYear} label="Ano" onChange={(e) => setSelectedYear(Number(e.target.value))}>
                  {(stats?.availableYears?.length ? stats.availableYears : availableYears).map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {/* 1. Monthly Pivot (Expenses by Category / Group) */}
          {allHomeExpenses.length > 0 && allExpenseGroups.length > 0 && (
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb:0 }}>
                  📅 Tabela mensal (despesas por categoria)
                  </Typography>
                  <Chip label={selectedYear} size="small" color="primary" variant="outlined" />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <EnhancedMonthlyPivot
                expenses={allHomeExpenses}
                groups={allExpenseGroups}
                formatCurrency={(n) => formatEuro(n)}
              />
            </Paper>
          )}

          {/* 2. Monthly Data Table */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb:0 }}>
                📋 Tabela de dados mensais
              </Typography>
              <Chip label={selectedYear} size="small" color="primary" variant="outlined" />
            </Box>
            <Divider sx={{ mb: 3 }} />
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Indicador</strong></TableCell>
                    {stats?.monthlyYearBreakdown?.map((month) => (
                      <TableCell key={month.month} align="center">
                        <strong>{month.monthName.substring(0, 3)}</strong>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: 'action.hover' }}>
                      <strong>TOTAL</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      Receitas
                    </TableCell>
                    {stats?.monthlyYearBreakdown?.map((month) => (
                      <TableCell key={month.month} align="center" sx={{ color: 'success.main' }}>
                        {formatEuro(month.income)}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: 'action.hover', color: 'success.main', fontWeight: 'bold' }}>
                      {formatEuro(stats?.monthlyYearBreakdown?.reduce((sum, month) => sum + month.income, 0) || 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      Despesas
                    </TableCell>
                    {stats?.monthlyYearBreakdown?.map((month) => (
                      <TableCell key={month.month} align="center" sx={{ color: 'error.main' }}>
                        {formatEuro(month.expenses)}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: 'action.hover', color: 'error.main', fontWeight: 'bold' }}>
                      {formatEuro(stats?.monthlyYearBreakdown?.reduce((sum, month) => sum + month.expenses, 0) || 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      Saldo
                    </TableCell>
                    {stats?.monthlyYearBreakdown?.map((month) => (
                      <TableCell key={month.month} align="center" sx={{ color: month.net >= 0 ? 'success.main' : 'error.main', fontWeight: 'medium' }}>
                        {formatEuro(month.net)}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ backgroundColor: 'action.hover', color: (stats?.monthlyYearBreakdown?.reduce((sum, month) => sum + month.net, 0) || 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                      {formatEuro(stats?.monthlyYearBreakdown?.reduce((sum, month) => sum + month.net, 0) || 0)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* 3. Monthly Breakdown Chart */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb:0 }}>
                  📈 Detalhe mensal
                </Typography>
                <Chip label={selectedYear} size="small" color="primary" variant="outlined" />
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={stats?.monthlyYearBreakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthName" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value: any, name: any) => [formatEuro(Number(value)), name]} />
                    <Legend />
                    <Bar dataKey="income" fill="#4caf50" name="Receitas" />
                    <Bar dataKey="expenses" fill="#f44336" name="Despesas" />
                    <Bar dataKey="net" fill="#2196f3" name="Saldo" />
                    <ReferenceLine y={0} stroke="#000" strokeDasharray="2 2" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

          {/* 3b. Category vs Month (after Monthly Breakdown) */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <CategoryMonthComparisonChart expenseType={expenseType} />
          </Paper>

          {/* 4. Category Usage Analysis */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb:0 }}>
                🥧 Análise de utilização das categorias
              </Typography>
              <Chip label={selectedYear} size="small" color="primary" variant="outlined" />
            </Box>
            <Divider sx={{ mb: 3 }} />
            <CategoryUsageChart />
          </Paper>

        </>
      )}
    </Box>
  );
};

const ExpensesPage: React.FC = () => (
  <ExpensesYearProvider>
    <ExpensesPageInner />
  </ExpensesYearProvider>
);

export default ExpensesPage;
